import React, { useState, useRef } from "react";
import FormStepper from "./FormStepper";
import DocumentUpload from "./DocumentUpload";
import PhoneInput from "react-phone-number-input";
import { useConfig } from "../contexts/ConfigContext";
import ReCAPTCHA from "react-google-recaptcha";
import "react-phone-number-input/style.css";

interface VehicleData {
  vehicleMake: string;
  vehicleModel: string;
  color: string;
  licensePlate: string;
}

interface FormData {
  // Personal Information
  firstName: string;
  lastName: string;
  email: string;
  phone: string | undefined;

  // Vehicle Information
  vehicles: VehicleData[];

  // Permit Details
  permitType: string;
  startDate: string;
}

interface FormErrors {
  [key: string]: string;
}

const VEHICLE_COLORS = [
  "Black",
  "White",
  "Silver",
  "Gray",
  "Red",
  "Blue",
  "Green",
  "Brown",
  "Gold",
  "Beige",
  "Orange",
  "Purple",
  "Other",
] as const;

interface UploadedFiles {
  [key: string]: File;
}

const PermitForm: React.FC = () => {
  const { config } = useConfig();
  const MAX_VEHICLES = config?.maxVehicles || 3;
  const maxSteps = config?.documents.enabled ? 4 : 3;
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [currentVehicle, setCurrentVehicle] = useState(0);
  const [recaptchaVerified, setRecaptchaVerified] = useState(false);
  const [showSubmitStep, setShowSubmitStep] = useState(false);  const [formData, setFormData] = useState<FormData>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    vehicles: [
      {
        vehicleMake: "",
        vehicleModel: "",
        color: "Black",
        licensePlate: "",
      },
    ],
    permitType: "monthly",
    startDate: new Date().toISOString().split("T")[0], // Set today as default
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFiles>({});
  const [fileErrors, setFileErrors] = useState<{ [key: string]: string }>({});
  // Helper function to validate personal information
  const validatePersonalInfo = (newErrors: FormErrors) => {
    const personalConfig = config?.mandatoryFields.personalInfo;
    if (personalConfig) {
      if (personalConfig.firstName && !formData.firstName.trim()) {
        newErrors.firstName = "First name is required";
      }
      if (personalConfig.lastName && !formData.lastName.trim()) {
        newErrors.lastName = "Last name is required";
      }
      if (personalConfig.email) {
        if (!formData.email.trim()) {
          newErrors.email = "Email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          newErrors.email = "Please enter a valid email address";
        }
      }
      if (personalConfig.phone && !formData.phone) {
        newErrors.phone = "Phone number is required";
      }
    }
    return Object.keys(newErrors).length === 0;
  };

  // Helper function to validate vehicle information
  const validateVehicleInfo = (
    vehicle: VehicleData,
    index?: number,
    newErrors: FormErrors = {}
  ) => {
    const vehicleConfig = config?.mandatoryFields.vehicleDetails;
    const suffix = index !== undefined ? `_${index}` : "";

    if (vehicleConfig) {
      if (vehicleConfig.make && !vehicle.vehicleMake.trim()) {
        newErrors[`vehicleMake${suffix}`] = "Vehicle make is required";
      }
      if (vehicleConfig.model && !vehicle.vehicleModel.trim()) {
        newErrors[`vehicleModel${suffix}`] = "Vehicle model is required";
      }
      if (vehicleConfig.color && !vehicle.color) {
        newErrors[`color${suffix}`] = "Vehicle color is required";
      }
    }

    // License plate is always mandatory
    if (!vehicle.licensePlate.trim()) {
      newErrors[`licensePlate${suffix}`] = "License plate is required";
    }

    return newErrors;
  };

  const validateDocuments = (): boolean => {
    if (!config?.documents.enabled) return true;

    const newErrors: { [key: string]: string } = {};
    const requiredDocs = config.documents.requirements.filter(
      (doc) => doc.required
    );

    requiredDocs.forEach((doc) => {
      if (!uploadedFiles[doc.id]) {
        newErrors[doc.id] = `${doc.name} is required`;
      }
    });

    setFileErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const validateStep = (step: number): boolean => {
    const newErrors: FormErrors = {};

    switch (step) {
      case 1: // Permit Details
        if (!formData.startDate) newErrors.startDate = "Start date is required";
        break;

      case 2: // Vehicle Details
        validateVehicleInfo(
          formData.vehicles[currentVehicle],
          undefined,
          newErrors
        );
        break;

      case 3: // Personal Information
        if (!validatePersonalInfo(newErrors)) {
          setErrors(newErrors);
          return false;
        }
        return true;

      case 4: // Documents
        return validateDocuments();
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Personal Information validation
    validatePersonalInfo(newErrors);

    // Vehicle information validation
    formData.vehicles.forEach((vehicle, index) => {
      validateVehicleInfo(vehicle, index, newErrors);
    });

    // Start date validation
    if (!formData.startDate) newErrors.startDate = "Start date is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const addVehicle = () => {
    if (formData.vehicles.length < MAX_VEHICLES) {
      setFormData((prev) => ({
        ...prev,
        vehicles: [
          ...prev.vehicles,
          {
            vehicleMake: "",
            vehicleModel: "",
            color: "Black",
            licensePlate: "",
          },
        ],
      }));
      setCurrentVehicle(formData.vehicles.length);
    }
  };

  const removeVehicle = (index: number) => {
    if (formData.vehicles.length > 1) {
      setFormData((prev) => ({
        ...prev,
        vehicles: prev.vehicles.filter((_, i) => i !== index),
      }));
      setCurrentVehicle(Math.max(0, currentVehicle - 1));
    }
  };
  const handleVehicleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    vehicleIndex: number
  ) => {
    const { name, value } = e.target;    // For license plate, restrict to alphanumeric and convert to uppercase
    if (name === "licensePlate") {
      const cleanValue = value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
      setFormData((prev) => ({
        ...prev,
        vehicles: prev.vehicles.map((vehicle, idx) =>
          idx === vehicleIndex ? { ...vehicle, [name]: cleanValue } : vehicle
        ),
      }));
    } 
    // For make and model, only restrict to alphanumeric
    else if (name === "vehicleMake" || name === "vehicleModel") {
      const cleanValue = value.replace(/[^a-zA-Z0-9]/g, "");
      setFormData((prev) => ({
        ...prev,
        vehicles: prev.vehicles.map((vehicle, idx) =>
          idx === vehicleIndex ? { ...vehicle, [name]: cleanValue } : vehicle
        ),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        vehicles: prev.vehicles.map((vehicle, idx) =>
          idx === vehicleIndex ? { ...vehicle, [name]: value } : vehicle
        ),
      }));
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    // Clear error when user starts typing in a field
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };
  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep === maxSteps) {
        setShowSubmitStep(true);
      } else {
        setCurrentStep((current) => Math.min(current + 1, maxSteps));
      }
    }
  };
  const handlePrevious = () => {
    if (showSubmitStep) {
      setShowSubmitStep(false);
    } else {
      setCurrentStep((current) => Math.max(current - 1, 1));
    }
  };

  const handleFileSelect = (docId: string, file: File) => {
    setUploadedFiles((prev) => ({
      ...prev,
      [docId]: file,
    }));
    setFileErrors((prev) => ({
      ...prev,
      [docId]: "",
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm() && validateDocuments()) {
      setIsSubmitting(true);
      try {
        // Get the reCAPTCHA token if enabled
        const recaptchaToken = config?.recaptcha.enabled
          ? await recaptchaRef.current?.getValue()
          : null;

        // Simulate API call
        await new Promise((resolve) => setTimeout(resolve, 1500));

        // In a real application, you would send this data to a server
        console.log("Form submitted:", {
          ...formData,
          files: Object.entries(uploadedFiles).map(([id, file]) => ({
            id,
            name: file.name,
            size: file.size,
            type: file.type,
          })),
          recaptcha: recaptchaToken,
        });

        setIsSubmitting(false);
        setSubmitted(true);
      } catch (error) {
        console.error("Error submitting form:", error);
        setIsSubmitting(false);
        alert(
          "There was an error submitting your application. Please try again later."
        );
      }
    }
  };
  const renderStep = () => {
    switch (currentStep) {
      case 1: // Permit Details
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold mb-4">Permit Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {" "}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Permit Type
                </label>
                <select
                  name="permitType"
                  value={formData.permitType}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-4 py-2"
                >
                  {config?.permitTypes.map((permit) => (
                    <option key={permit.id} value={permit.id}>
                      {permit.label} - ${permit.price.toFixed(2)}
                    </option>
                  ))}
                </select>
                {config?.permitTypes.find((p) => p.id === formData.permitType)
                  ?.description && (
                  <p className="mt-1 text-sm text-gray-500">
                    {
                      config.permitTypes.find(
                        (p) => p.id === formData.permitType
                      )?.description
                    }
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Start Date
                </label>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                  min={new Date().toISOString().split("T")[0]}
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-4 py-2 ${
                    errors.startDate ? "border-red-500" : ""
                  }`}
                />
                {errors.startDate && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.startDate}
                  </p>
                )}
              </div>
            </div>
          </div>
        );

      case 2: // Vehicle Details
        const currentVehicleData = formData.vehicles[currentVehicle];
        return (
          <div className="space-y-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Vehicle Information</h2>
              <div className="flex items-center space-x-2">
                {formData.vehicles.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeVehicle(currentVehicle)}
                    className="px-3 py-1 text-sm text-red-600 border border-red-600 rounded hover:bg-red-50"
                  >
                    Remove Vehicle
                  </button>
                )}
                {formData.vehicles.length < MAX_VEHICLES && (
                  <button
                    type="button"
                    onClick={addVehicle}
                    className="px-3 py-1 text-sm text-blue-600 border border-blue-600 rounded hover:bg-blue-50"
                  >
                    Add Another Vehicle
                  </button>
                )}
              </div>
            </div>

            {formData.vehicles.length > 1 && (
              <div className="flex gap-2 mb-4">
                {formData.vehicles.map((_, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => setCurrentVehicle(index)}
                    className={`px-3 py-1 rounded ${
                      currentVehicle === index
                        ? "bg-blue-600 text-white"
                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                  >
                    Vehicle {index + 1}
                  </button>
                ))}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Vehicle Make
                </label>                <input
                  type="text"
                  name="vehicleMake"
                  value={currentVehicleData.vehicleMake}
                  onChange={(e) => handleVehicleChange(e, currentVehicle)}
                  pattern="[a-zA-Z0-9]*"
                  title="Only letters and numbers are allowed. Any case is allowed."
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-4 py-2 ${
                    errors.vehicleMake ? "border-red-500" : ""
                  }`}
                />
                {errors.vehicleMake && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.vehicleMake}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Vehicle Model
                </label>
                <input
                  type="text"
                  name="vehicleModel"
                  value={currentVehicleData.vehicleModel}
                  onChange={(e) => handleVehicleChange(e, currentVehicle)}
                  pattern="[a-zA-Z0-9]*"
                  title="Only letters and numbers are allowed. Any case is allowed."
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-4 py-2 ${
                    errors.vehicleModel ? "border-red-500" : ""
                  }`}
                />
                {errors.vehicleModel && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.vehicleModel}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Vehicle Color
                </label>
                <select
                  name="color"
                  value={currentVehicleData.color}
                  onChange={(e) => handleVehicleChange(e, currentVehicle)}
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-4 py-2 ${
                    errors.color ? "border-red-500" : ""
                  }`}
                >
                  {VEHICLE_COLORS.map((color) => (
                    <option key={color} value={color}>
                      {color}
                    </option>
                  ))}
                </select>
                {errors.color && (
                  <p className="mt-1 text-sm text-red-500">{errors.color}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  License Plate
                </label>
                <input
                  type="text"
                  name="licensePlate"
                  value={currentVehicleData.licensePlate}
                  onChange={(e) => handleVehicleChange(e, currentVehicle)}
                  pattern="[A-Za-z0-9]*"
                  title="Only letters and numbers are allowed"
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-4 py-2 ${
                    errors.licensePlate ? "border-red-500" : ""
                  }`}
                />
                {errors.licensePlate && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.licensePlate}
                  </p>
                )}
              </div>
            </div>
          </div>
        );

      case 3: // Personal Information
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">Personal Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  First Name
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-4 py-2 ${
                    errors.firstName ? "border-red-500" : ""
                  }`}
                />
                {errors.firstName && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.firstName}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Last Name
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-4 py-2 ${
                    errors.lastName ? "border-red-500" : ""
                  }`}
                />
                {errors.lastName && (
                  <p className="mt-1 text-sm text-red-500">{errors.lastName}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-4 py-2 ${
                    errors.email ? "border-red-500" : ""
                  }`}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-500">{errors.email}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Phone Number
                </label>
                <PhoneInput
                  international
                  defaultCountry="US"
                  value={formData.phone}
                  onChange={(value) =>
                    setFormData((prev) => ({ ...prev, phone: value }))
                  }
                  className={`mt-1 ${errors.phone ? "!border-red-500" : ""}`}
                />
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-500">{errors.phone}</p>
                )}
              </div>
            </div>
          </div>
        );

      case 4: // Documents
        if (!config?.documents.enabled) return null;
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold mb-4">Required Documents</h2>
            <p className="text-gray-600 mb-6">
              Please provide the following documentation to complete your
              application. Required documents are marked with an asterisk (*).
            </p>
            <div className="space-y-6">
              {config.documents.requirements.map((requirement) => (
                <DocumentUpload
                  key={requirement.id}
                  requirement={requirement}
                  onFileSelect={handleFileSelect}
                  selectedFile={uploadedFiles[requirement.id]}
                  error={fileErrors[requirement.id]}
                />
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Success message after submission
  if (submitted) {
    return (
      <div className="text-center py-12">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-6">
          <svg
            className="w-8 h-8 text-green-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <h3 className="text-2xl font-bold text-gray-800 mb-2">
          Application Submitted!
        </h3>
        <p className="text-gray-600 mb-6">
          Thank you for registering for a parking permit. We will review your
          application and contact you shortly.
        </p>
        <button
          onClick={() => {
            setSubmitted(false);
            setFormData({
              firstName: "",
              lastName: "",
              email: "",
              phone: "",
              vehicles: [
                {
                  vehicleMake: "",
                  vehicleModel: "",
                  color: "Black",
                  licensePlate: "",
                },
              ],
              permitType: "monthly",
              startDate: "",
            });
            setCurrentStep(1);
          }}
          className="px-6 py-3 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
        >
          Submit Another Application
        </button>
      </div>
    );
  }
  return (
    <div className="space-y-8">
      {!showSubmitStep && <FormStepper currentStep={currentStep} />}

      <form onSubmit={handleSubmit} className="space-y-6">
        {showSubmitStep ? (
          <div className="space-y-8 text-center">
            <h2 className="text-xl font-semibold mb-4">Submit Application</h2>
            <p className="text-gray-600 mb-6">
              Please complete the verification below to submit your application.
            </p>
            {config?.recaptcha.enabled && (
              <div className="flex flex-col items-center gap-4">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={config.recaptcha.siteKey}
                  onChange={(value) => {
                    setRecaptchaVerified(!!value);
                    if (errors.recaptcha) {
                      setErrors((prev) => ({ ...prev, recaptcha: "" }));
                    }
                  }}
                />
                {errors.recaptcha && (
                  <p className="text-sm text-red-500">{errors.recaptcha}</p>
                )}
              </div>
            )}
          </div>
        ) : (
          renderStep()
        )}

        <div className="flex justify-between pt-6">
          <button
            type="button"
            onClick={handlePrevious}
            className="px-6 py-2 border border-gray-300 text-gray-700 font-medium rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            Previous
          </button>

          <div>
            {!showSubmitStep ? (
              <button
                type="button"
                onClick={handleNext}
                className="px-6 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                Next
              </button>
            ) : (
              <button
                type="submit"
                disabled={
                  isSubmitting ||
                  (config?.recaptcha.enabled && !recaptchaVerified)
                }
                className={`px-6 py-2 ${
                  isSubmitting ||
                  (config?.recaptcha.enabled && !recaptchaVerified)
                    ? "bg-blue-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700"
                } text-white font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors flex items-center`}
              >
                {isSubmitting ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Processing...
                  </>
                ) : (
                  "Submit Application"
                )}
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default PermitForm;
